/* istanbul ignore file */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import {
  getScheduledReportSettings,
  updateScheduledReportSettings,
  sendScheduledReportNow,
  type ReportPeriod,
  type ReportScope,
  type ScheduledReportConfig,
} from "@app/services/adminSettingsApi";

const REPORTS_QUERY_KEY = ["admin", "scheduled-report-settings"];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => hour);

const UTC_OFFSET_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "UTC +0" },
  { value: 1, label: "UTC +1" },
  { value: 2, label: "UTC +2" },
  { value: 3, label: "UTC +3" },
  { value: 4, label: "UTC +4" },
];

const DAY_OF_WEEK_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Monday" },
  { value: 1, label: "Tuesday" },
  { value: 2, label: "Wednesday" },
  { value: 3, label: "Thursday" },
  { value: 4, label: "Friday" },
  { value: 5, label: "Saturday" },
  { value: 6, label: "Sunday" },
];

const SCOPE_OPTIONS: { value: ReportScope; label: string }[] = [
  { value: "per_user", label: "Per-user profit (each user sees their own result)" },
  { value: "global", label: "Global summary (same report for everyone)" },
];

const PERIOD_LABELS: Record<ReportPeriod, string> = {
  daily: "Daily Report",
  weekly: "Weekly Report",
  monthly: "Monthly Report",
};

const PERIOD_DESCRIPTIONS: Record<ReportPeriod, string> = {
  daily: "Sent once per day at the chosen local hour.",
  weekly: "Sent once per week on the chosen day and hour.",
  monthly: "Sent once per month on the chosen day and hour.",
};

interface AdminScheduledReportsCardProps {
  isAdmin: boolean;
}

interface PeriodSectionProps {
  config: ScheduledReportConfig;
  onSaved: () => void;
}

function PeriodSection({ config, onSaved }: PeriodSectionProps): JSX.Element {
  const [enabled, setEnabled] = useState(false);
  const [hour, setHour] = useState(9);
  const [utcOffset, setUtcOffset] = useState(3);
  const [scope, setScope] = useState<ReportScope>("per_user");
  const [channels, setChannels] = useState<string[]>(["telegram", "email"]);
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(Boolean(config.enabled));
    setHour(config.hour ?? 9);
    setUtcOffset(config.utc_offset ?? 3);
    setScope((config.scope as ReportScope) ?? "per_user");
    if (Array.isArray(config.channels) && config.channels.length > 0) {
      setChannels(config.channels);
    }
    setDayOfWeek(config.day_of_week ?? 0);
    setDayOfMonth(config.day_of_month ?? 1);
  }, [config]);

  const saveMutation = useMutation({
    mutationFn: updateScheduledReportSettings,
    onMutate: () => {
      setError(null);
      setStatusMessage(null);
    },
    onSuccess: (result) => {
      setStatusMessage(result.message);
      onSaved();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to save settings.");
    },
  });

  const sendNowMutation = useMutation({
    mutationFn: sendScheduledReportNow,
    onMutate: () => {
      setError(null);
      setStatusMessage(null);
    },
    onSuccess: (result) => {
      const summary = result.summary;
      setStatusMessage(
        summary
          ? `Report sent to ${summary.total} users (${summary.sent} sent, ${summary.skipped} skipped, ${summary.failed} failed).`
          : result.message,
      );
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to send report.");
    },
  });

  const toggleChannel = (channel: string) => {
    setChannels((prev) =>
      prev.includes(channel)
        ? prev.filter((c) => c !== channel)
        : [...prev, channel],
    );
  };

  const handleSave = () => {
    if (channels.length === 0) {
      setError("Select at least one delivery channel.");
      return;
    }
    saveMutation.mutate({
      period: config.period,
      enabled,
      hour,
      utc_offset: utcOffset,
      scope,
      channels,
      day_of_week: config.period === "weekly" ? dayOfWeek : undefined,
      day_of_month: config.period === "monthly" ? dayOfMonth : undefined,
    });
  };

  return (
    <div className="report-period-section">
      <div className="report-period-section__header">
        <div>
          <h4 className="report-period-section__title">
            {PERIOD_LABELS[config.period]}
          </h4>
          <p className="report-period-section__desc">
            {PERIOD_DESCRIPTIONS[config.period]}
          </p>
        </div>
        <label className="integrations-checkbox" style={{ margin: 0 }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          Enabled
        </label>
      </div>

      <form
        className="integrations-form"
        onSubmit={(event) => {
          event.preventDefault();
          handleSave();
        }}
      >
        <label>
          Send hour (local time)
          <select
            value={hour}
            onChange={(event) => setHour(Number(event.target.value))}
          >
            {HOUR_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {String(value).padStart(2, "0")}:00
              </option>
            ))}
          </select>
        </label>

        <label>
          Timezone (UTC offset)
          <select
            value={utcOffset}
            onChange={(event) => setUtcOffset(Number(event.target.value))}
          >
            {UTC_OFFSET_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        {config.period === "weekly" && (
          <label>
            Send day
            <select
              value={dayOfWeek}
              onChange={(event) => setDayOfWeek(Number(event.target.value))}
            >
              {DAY_OF_WEEK_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {config.period === "monthly" && (
          <label>
            Send day of month
            <select
              value={dayOfMonth}
              onChange={(event) => setDayOfMonth(Number(event.target.value))}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        )}

        <label>
          Report content
          <select
            value={scope}
            onChange={(event) => setScope(event.target.value as ReportScope)}
          >
            {SCOPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div className="integrations-checkbox">
          <input
            type="checkbox"
            checked={channels.includes("telegram")}
            onChange={() => toggleChannel("telegram")}
          />
          Telegram
        </div>
        <div className="integrations-checkbox">
          <input
            type="checkbox"
            checked={channels.includes("email")}
            onChange={() => toggleChannel("email")}
          />
          Email
        </div>

        {config.last_sent ? (
          <p className="report-period-section__last">
            Last sent: {config.last_sent}
          </p>
        ) : null}

        {error ? <p className="integrations-error">{error}</p> : null}
        {statusMessage ? (
          <p className="integrations-summary" style={{ marginTop: "8px" }}>
            {statusMessage}
          </p>
        ) : null}

        <div className="integrations-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? "Saving..." : "Save settings"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={sendNowMutation.isPending}
            onClick={() => sendNowMutation.mutate(config.period)}
          >
            {sendNowMutation.isPending ? "Sending..." : "Send now (test)"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function AdminScheduledReportsCard({
  isAdmin,
}: AdminScheduledReportsCardProps): JSX.Element | null {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: REPORTS_QUERY_KEY,
    queryFn: getScheduledReportSettings,
    enabled: isAdmin,
    staleTime: 30_000,
  });

  if (!isAdmin) {
    return null;
  }

  const periods = data?.periods ?? [];

  return (
    <Card
      className="overview-card integrations-card"
      title="Automatic Reports"
      subtitle="Send daily, weekly, and monthly reports to all active users. Each type can be enabled independently."
    >
      <div className="section-legend">
        <span className="section-legend__dot" />
        <div>
          Enable any combination of periods. Each report is sent once per its
          own cycle at the chosen local time.
        </div>
      </div>

      {periods.length === 0 ? (
        <div className="integrations-empty">Loading report settings...</div>
      ) : (
        periods.map((config) => (
          <PeriodSection
            key={config.period}
            config={config}
            onSaved={() =>
              queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEY })
            }
          />
        ))
      )}
    </Card>
  );
}
